import dayjs from 'dayjs';
import lodashFilter from 'lodash/filter';
import lodashFind from 'lodash/find';
import lodashIsEmpty from 'lodash/isEmpty';

import constants from '../../../Config/constants';
import Service from '../../../Service';

import { baseApi } from '../baseApi';
import { ApiArgs, Checkout, PaginatedResult } from '../types';

import { CheckoutEventPayload, EventDetails, EventPayload } from './types';

export const storeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEvents: builder.query<
      PaginatedResult<EventDetails>,
      ApiArgs<{ isNextMonth?: boolean }, EventPayload>
    >({
      query: (params) => {
        const { store_id, ...payload } = params.payload;
        return {
          url: `/reservation/${store_id}/events`,
          method: 'GET',
          headers: { 'x-api-version': '1' },
          params: payload,
        };
      },
      transformErrorResponse: (error: any) => {
        return Service.handleErrorMessage(error?.data?.message);
      },
      transformResponse: (
        data: PaginatedResult<EventDetails>,
        _meta,
        params,
      ) => ({
        ...data,
        result: params?.data?.isNextMonth
          ? // if filter by next month, return only the event that is for next month and until next month
            lodashFilter(data.result, (e) => {
              const nextMonth = dayjs().get('month') + 1;
              const eStartMonth = dayjs(e.start_date).get('month');
              const eEndMonth = dayjs(e.end_date).get('month');
              return eStartMonth >= nextMonth || eEndMonth >= nextMonth;
            }).sort(
              // after that, sort the event by next month first then the event that is until next month
              (curr, next) =>
                dayjs(next.start_date).valueOf() -
                dayjs(curr.start_date).valueOf(),
            )
          : data.result, // return the normal result
      }),
    }),
    getEventDetails: builder.query<EventDetails, ApiArgs<void, string>>({
      query: (params) => ({
        url: `/reservation/events/${params.payload}`,
        method: 'GET',
        headers: { 'x-api-version': '1' },
      }),
      transformResponse: (data: EventDetails, _meta) => {
        if (lodashIsEmpty(data)) return data;
        return {
          ...data,
          tickets: lodashFilter(data.tickets, (t) => !isNaN(t.item.price)) // filter out NaN price of tickets
            .sort((a, b) => (a.item.price < b.item.price ? -1 : 1)), // sort ticket by price lowest to highest
          // sort time_slot be earliest to later
          time_slots: data.time_slots.sort((a, b) =>
            a.start_time < b.start_time ? -1 : 1,
          ),
        };
      },
    }),
    addToCartEvent: builder.mutation<any, any>({
      query: (body) => ({
        url: `/cart`,
        method: 'POST',
        body: body,
        headers: { 'x-api-version': '1' },
      }),
      transformErrorResponse: (result) => {
        const errData = result.data as any;
        return Service.handleErrorMessage(errData.message) as string;
      },
    }),
    checkoutEvent: builder.mutation<
      Checkout,
      ApiArgs<void, CheckoutEventPayload>
    >({
      query: (params) => ({
        url: `/checkout/event`,
        method: 'POST',
        body: params.payload,
        headers: { 'x-api-version': '1' },
      }),
      transformErrorResponse: (result) => {
        const errData = result.data as any;
        if (Array.isArray(errData.message)) {
          const soldOutKeyValues = Object.keys(constants.EVENT_SOLDOUT).map(
            (e) => constants.EVENT_SOLDOUT[e], // get key value
          ); // overall or selected date
          // loop through sold out keys and return the word that matched
          return lodashFind(
            soldOutKeyValues,
            (word) => lodashFind(errData.message, (msg) => msg.includes(word)), // find the key on error messages
          );
        }
        return Service.handleErrorMessage(errData.message) as string;
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetEventsQuery,
  useGetEventDetailsQuery,
  useAddToCartEventMutation,
  useCheckoutEventMutation,
} = storeApi;
